import { useState } from 'react';

import EBTCardSummary from 'features/shop/cart/EBTPaymentFlow/shared/EBTCardSummary';
import {
    CurrentAmount,
    SnapEligibleTotal,
    StepButton,
    StepCard,
    StepHeading,
} from 'features/shop/cart/EBTPaymentFlow/shared/FlowPresentation';
import { getAmountErrorMessage } from 'features/shop/cart/EBTPaymentFlow/shared/utils';
import EBTPaymentElement from 'shared/ebt/EBTPaymentElement';
import useEBTBalanceCheck from 'shared/ebt/useEBTBalanceCheck';

/**
 * EditEBTPaymentAmount component allows users to modify the EBT payment amount
 * in the EBT payment flow.
 *
 * @param {Object} props - The component props
 * @param {Object} props.cartData - Cart data containing SNAP-eligible total and other cart information
 * @param {number} props.currentAmount - The current EBT payment amount that will be modified
 * @param {Object} props.currentStepData - Configuration for the current step including balance check and focus settings
 * @param {Object} props.ebtCard - EBT card information including last4 digits
 * @param {Object} props.flowData - Current flow data including current amount and new amount
 * @param {Function} props.handleNext - Function to navigate to the next step in the flow
 * @param {Function} props.setFlowData - Function to update the flow data state
 * @returns {JSX.Element} The rendered EditEBTPaymentAmount component
 */
function EditEBTPaymentAmount({
    cartData,
    currentAmount,
    currentStepData,
    ebtCard,
    flowData,
    handleNext,
    setFlowData,
}) {
    const snapEligibleTotal = parseFloat(cartData?.snapEligibleTotal || 0);
    const { enableBalanceCheck = true, focusAmountField = false } = currentStepData || {};
    const { balance } = useEBTBalanceCheck(ebtCard?.last4);

    const [amountField, setAmountField] = useState({
        value: null,
        isValid: false,
        error: '',
    });

    const validateAmountField = () => {
        const amountErrorMessage = getAmountErrorMessage({
            allowZero: true,
            amount: parseFloat(amountField.value),
            snapEligibleTotal,
            currentAmount,
            snapBalance: balance?.snap,
        });

        setAmountField((prev) => ({ ...prev, error: amountErrorMessage }));
        return !amountErrorMessage;
    };

    const handleAmountChange = (value) => {
        setAmountField((prev) => ({
            ...prev,
            value,
            error: '', // Clear error when user types
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateAmountField()) return;
        setFlowData({ ...flowData, newAmount: Number(amountField.value) });
        handleNext('EditEBTPaymentConfirm');
    };

    return (
        <div>
            <StepHeading>Edit EBT Amount</StepHeading>
            <div className="my-20 space-y-5">
                <SnapEligibleTotal total={cartData?.snapEligibleTotal} />
                <CurrentAmount amount={currentAmount} />
            </div>
            <StepCard>
                <div className="mb-20 md:flex md:justify-between">
                    <EBTCardSummary
                        last4={ebtCard?.last4}
                        onCheckBalanceClick={() => handleNext('CheckBalance')}
                        enableBalanceCheck={enableBalanceCheck}
                    />
                </div>
                <form onSubmit={handleSubmit}>
                    <EBTPaymentElement
                        label="New Total EBT Amount"
                        onChange={handleAmountChange}
                        error={amountField.error}
                        autoFocus={focusAmountField}
                    />
                    <StepButton>Review Change</StepButton>
                </form>
            </StepCard>
        </div>
    );
}

export default EditEBTPaymentAmount;
